import { TransactionStatus, TransactionTypeString } from '../models/Transactions';
import { COIN_TICKER, BLOCKCHAINS_NAMES } from '../models/Crypto';
import { ICryptoCoin, COIN_FULL_NAME, ICryptoPrices, COIN_COLOR } from '../models/Crypto';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { ICryptoBalance, ICryptoPortfolio } from '../models/Crypto';
import { ApiService } from '../services/api.service';
import { AuthService } from '../abstraction/auth.service';
import { cryptoTickers } from '../utils/cryptoTickers';
import { COIN } from '../models/Order';
import { Utils } from '../utils/Utils';
import { ITransaction } from '../models/Transactions';
import BigNumber from 'bignumber.js';
import { ACCOUNT_CURRENCY } from '../models/User';
import { IMinOperation } from '../../public/services/utils/public-data';

@Injectable({
  providedIn: 'root',
})
export class CryptoStore {
  private cryptoPrices$ = new BehaviorSubject<ICryptoPrices>({});
  private allCryptos$ = new BehaviorSubject<ICryptoCoin[][]>([]);
  private _stableCryptos = new BehaviorSubject<ICryptoCoin[]>([]);
  private _volatilCryptos = new BehaviorSubject<ICryptoCoin[]>([]);

  private cryptoBalance$ = new BehaviorSubject<ICryptoBalance>({});
  private validSwapPairs$ = new BehaviorSubject<string[]>([]);
  private availableSwapCoins$ = new BehaviorSubject<string[]>([]);
  private withdrawableCoins$ = new BehaviorSubject<COIN_TICKER[]>([]);
  private _minOperation: WritableSignal<IMinOperation> = signal(undefined);
  private _errorLimit: WritableSignal<boolean> = signal(false);

  // Por ahora este dato no viene del back
  private _blockchains = [
    BLOCKCHAINS_NAMES.ETHEREUM,
    BLOCKCHAINS_NAMES.BSC,
    BLOCKCHAINS_NAMES.TERRA2,
    BLOCKCHAINS_NAMES.POLYGON,
  ];

  private _depositableCoins = {
    BSC: [
      COIN_TICKER.DAI,
      COIN_TICKER.USDT,
      COIN_TICKER.BUSD,
      COIN_TICKER.USDC,
      COIN_TICKER.BTC,
      COIN_TICKER.BNB,
      COIN_TICKER.ETH,
      COIN_TICKER.MATIC,
      COIN_TICKER.SOL,
    ],
    ETH: [COIN_TICKER.DAI, COIN_TICKER.ETH, COIN_TICKER.USDT, COIN_TICKER.USDC],
    TERRA: [COIN_TICKER.USTC, COIN_TICKER.LUNC],
    POLYGON: [COIN_TICKER.USDT, COIN_TICKER.MATIC, COIN_TICKER.USDC],
  };

  private _marketHiddenCoinPairs = [
    'AXS_ARS',
    'AXS_USD',
    'MANA_ARS',
    'MANA_USD',
    'ENJ_ARS',
    'ENJ_USD',
    'SAND_ARS',
    'SAND_USD',
    'AUST_ARS',
    'AUST_USD',
    'PSG_ARS',
    'PSG_USD',
    'ATM_ARS',
    'ATM_USD',
    'BAR_ARS',
    'BAR_USD',
    'CITY_ARS',
    'CITY_USD',
    'DAICOMM_ARS',
    'DAICOMM_USD',
    'USDTCOMM_ARS',
    'USDTCOMM_USD',
    'USTCOMM_ARS',
    'USTCOMM_USD',
    'BUSDCOMM_ARS',
    'BUSDCOMM_USD',
    'USDCCOMM_ARS',
    'USDCCOMM_USD',
    'LUNA2_ARS',
    'LUNA2_USD',
    'UST_ARS',
    'UST_USD',
    'NUARSCOMM_ARS',
    'NUARSCOMM_USD',
    'NUARS_USD',
    'WLD_ARS',
    'WLD_USD',
  ];

  private _swapHiddenCoins = [
    COIN_TICKER.AUST,
    COIN_TICKER.AXS,
    COIN_TICKER.SDAI,
    COIN_TICKER.MANA,
    COIN_TICKER.ENJ,
    COIN_TICKER.SAND,
    COIN_TICKER.PSG,
    COIN_TICKER.ATM,
    COIN_TICKER.BAR,
    COIN_TICKER.CITY,
    COIN_TICKER.DAICOMM,
    COIN_TICKER.USDTCOMM,
    COIN_TICKER.USTCOMM,
    COIN_TICKER.LUNA,
    COIN_TICKER.UST,
    COIN_TICKER.LUNA2,
    COIN_TICKER.LUNC,
    COIN_TICKER.USTC,
    COIN_TICKER.NUARS,
    COIN_TICKER.BUSDCOMM,
    COIN_TICKER.USDCCOMM,
    COIN_TICKER.NUARSCOMM,
    COIN_TICKER.WLD,
  ];

  constructor(private api: ApiService, private authService: AuthService) {
    this.loadMinOperationFromLS();
  }

  get cryptoPrices() {
    return this.cryptoPrices$;
  }

  get cryptoBalance() {
    return this.cryptoBalance$;
  }

  get cryptoList() {
    return this.allCryptos$;
  }

  get withdrawableCoins() {
    return this.withdrawableCoins$;
  }

  get blockchains() {
    return this._blockchains;
  }

  get validSwapPairs() {
    return this.validSwapPairs$;
  }

  get availableSwapCoins() {
    return this.availableSwapCoins$;
  }

  get stableCryptos() {
    return this._stableCryptos;
  }

  get volatilCryptos() {
    return this._volatilCryptos;
  }

  get depositableCoins() {
    return this._depositableCoins;
  }

  get minOperation() {
    if (this._minOperation()) {
      localStorage.setItem('min-operation', JSON.stringify(this._minOperation()));
    }
    return this._minOperation;
  }

  get errorLimit() {
    return this._errorLimit;
  }

  getCryptoPrices() {
    this.api.getAllPrices().subscribe((res) => {
      let prices: ICryptoPrices = res;
      if (this.authService.loggedUser?.isCommerce) {
        prices = {
          ...prices,
          DAI_ARS: {
            ...prices.DAICOMM_ARS,
            ticker: 'DAI_ARS',
            originalValue: { buy: prices.DAI_ARS.buy, sell: prices.DAI_ARS.sell },
          },
          DAI_USD: { ...prices.DAICOMM_USD, ticker: 'DAI_USD' },
          UST_ARS: { ...prices.USTCOMM_ARS, ticker: 'UST_ARS' },
          UST_USD: { ...prices.USTCOMM_USD, ticker: 'UST_USD' },
          USDT_ARS: { ...prices.USDTCOMM_ARS, ticker: 'USDT_ARS' },
          USDT_USD: { ...prices.USDTCOMM_USD, ticker: 'USDT_USD' },
          NUARS_ARS: { ...prices.NUARSCOMM_ARS, ticker: 'NUARS_ARS' },
          NUARS_USD: { ...prices.NUARSCOMM_USD, ticker: 'NUARS_USD' },
          BUSD_ARS: { ...prices.BUSDCOMM_ARS, ticker: 'BUSD_ARS' },
          BUSD_USD: { ...prices.BUSDCOMM_USD, ticker: 'BUSD_USD' },
          USDC_ARS: { ...prices.USDCCOMM_ARS, ticker: 'USDC_ARS' },
          USDC_USD: { ...prices.USDCCOMM_USD, ticker: 'USDC_USD' },
        };
      }
      prices = {
        ...prices,
        LUNC_ARS: { ...prices.LUNA_ARS, ticker: 'LUNC_ARS', coin: 'LUNC_ARS' },
        LUNC_USD: { ...prices.LUNA_USD, ticker: 'LUNC_USD', coin: 'LUNC_USD' },
        LUNC_DAI: { ...prices.LUNA_DAI, ticker: 'LUNC_DAI', coin: 'LUNC_DAI' },
        USTC_USD: { ...prices.UST_USD, ticker: 'USTC_USD', coin: 'USTC_USD' },
        // USTC_ARS: { ...prices.UST_ARS, ticker: 'USTC_ARS', coin: 'USTC_ARS' },
        // USTC_DAI: { ...prices.UST_DAI, ticker: 'USTC_DAI', coin: 'USTC_DAI' },
      };
      prices = {
        ...prices,
        LUNA_ARS: { ...prices.LUNA2_ARS, ticker: 'LUNA_ARS', coin: 'LUNA_ARS' },
        LUNA_USD: { ...prices.LUNA2_USD, ticker: 'LUNA_USD', coin: 'LUNA_USD' },
      };
      this.cryptoPrices$.next(prices);

      // this.initializeMarketCoins();
      // this.setPortfolioByCryptoBalance();
      // this.setSwapAvailableCoins();
    });
  }

  isHiddenInMarket(ticker: string) {
    return this._marketHiddenCoinPairs.includes(ticker);
  }

  setValidSwapPairs(statusUnavailableCoins?: string[]) {
    const prices = this.cryptoPrices.value;
    const validPairs = Object.keys(prices).filter(
      (pair) =>
        pair.split('_')[1] !== 'ARS' &&
        pair.split('_')[1] !== 'USD' &&
        ![...new Set(statusUnavailableCoins)].includes(pair),
    );
    this.validSwapPairs.next(validPairs);
  }

  setSwapAvailableCoins() {
    this.getOrdersStatus().subscribe((status) => {
      let coins = Object.keys(this.cryptoPrices$.value);
      const availableCoins = [];
      const statusUnavailableCoins = [];
      Object.values(status.BUY).forEach((coins) =>
        coins.forEach((coin) => {
          statusUnavailableCoins.push(coin);
        }),
      );
      Object.values(status.SELL).forEach((coins) =>
        coins.forEach((coin) => {
          statusUnavailableCoins.push(coin);
        }),
      );
      this.setValidSwapPairs(statusUnavailableCoins);
      coins = [...new Set(coins)].filter(
        (coin_pair) => ![...new Set(statusUnavailableCoins)].includes(coin_pair),
      );
      coins.forEach((coin) => {
        const coinArr = coin.split('_');
        if (!this._swapHiddenCoins.includes(coinArr[0] as COIN_TICKER)) {
          availableCoins.push(coinArr[0]);
        }
        if (
          coinArr[1] !== 'ARS' &&
          coinArr[1] !== 'USD' &&
          !this._swapHiddenCoins.includes(coinArr[0] as COIN_TICKER)
        ) {
          availableCoins.push(coinArr[1]);
        }
      });
      this.availableSwapCoins.next([...new Set(availableCoins)].filter((coin) => coin));
    });
  }

  getCryptoPortfolio() {
    combineLatest([this.cryptoPrices, this.cryptoBalance]).subscribe(
      (res: [ICryptoPrices, ICryptoBalance]) => {
        const [prices, balance] = res;
        const cryptoBalance = Object.keys(balance);
        if ((prices.length, cryptoBalance.length)) {
          let cryptoPortfolio: ICryptoPortfolio[] = [];
          cryptoBalance.forEach((coin) => {
            const pricesArray = Object.values(prices);
            pricesArray.forEach((price) => {
              if (coin === price.coin) {
                const amountValue = new BigNumber(balance[coin].amount);
                const stakeBalanceValue = new BigNumber(balance[coin].stakeBalance);
                const sellPriceValue = new BigNumber(price.sell);

                const portfolioItem: ICryptoPortfolio = {
                  name: cryptoTickers[coin],
                  ticker: coin,
                  porcentage: 20,
                  stocks: amountValue.toString(),
                  balance: amountValue
                    .plus(stakeBalanceValue)
                    .multipliedBy(sellPriceValue)
                    .toString(),
                  stakeBalance: stakeBalanceValue.toNumber(),
                  price: sellPriceValue.plus(new BigNumber(price.sell)).dividedBy(2).toString(),
                  totalARSinvested: 0,
                  capitalChange: 0,
                  capitalChangeP: 0,
                };
                cryptoPortfolio.push(portfolioItem);
              }
            });
          });

          cryptoPortfolio.forEach((port) => {
            if (port.ticker.includes('_USD')) {
              const realTicker = port.ticker.replace('_USD', '');
              const index = cryptoPortfolio.map((port) => port.ticker).indexOf(realTicker);
              cryptoPortfolio[index].stocks = new BigNumber(cryptoPortfolio[index].stocks)
                .plus(new BigNumber(port.stocks))
                .toString();
            }
          });
          cryptoPortfolio = cryptoPortfolio.filter((port) => !port.ticker.includes('_USD'));
          const totalInvestment = cryptoPortfolio.reduce(
            (acu, current) => acu + Number(current.balance) + current.stakeBalance,
            0,
          );

          cryptoPortfolio = cryptoPortfolio.map((port) => {
            const inv = { ...port };
            inv.porcentage = ((Number(inv.balance) + inv.stakeBalance) * 100) / totalInvestment;
            return inv;
          });

          this.authService.myCryptoPortfolio.next(cryptoPortfolio);
        }
      },
    );
  }

  public getByTickerCurr(ticker: string, curr = 1) {
    return +this.cryptoPrices$.value[ticker + '_DAI'].buy;
  }

  getCryptoPriceByCoin(coin: string) {
    if (this.authService.loggedUser?.isCommerce) {
      const coinArr = coin.split('_');
      if (this.isStableCoin(coinArr[0])) {
        coin = coin.replace(coinArr[0], `${coinArr[0]}COMM`);
      }
    }
    return this.api.getPriceByCoin(coin);
  }

  getCryptoPriceByCoinWld(coin: string) {
    if (this.authService.loggedUser?.isCommerce) {
      const coinArr = coin.split('_');
      if (this.isStableCoin(coinArr[0])) {
        coin = coin.replace(coinArr[0], `${coinArr[0]}COMM`);
      }
    }
    return this.api.getPriceByCoin(coin);
  }

  getTotalCryptoBalance() {
    let totalCryptoInArs = 0;
    let totalCryptoInUsd = 0;
    const balances = this.cryptoBalance.value;
    if (balances) {
      for (const [key, value] of Object.entries(balances)) {
        if (key === 'DAI') {
          totalCryptoInArs +=
            (parseFloat(value.amount) + value.stakeBalance) * this.getByTickerCurr(value.ticker);
          totalCryptoInUsd += parseFloat(value.amount) + value.stakeBalance;
        } else {
          totalCryptoInArs +=
            (parseFloat(value.amount) + value.stakeBalance) * this.getByTickerCurr(value.ticker);
          totalCryptoInUsd +=
            (parseFloat(value.amount) + value.stakeBalance) * this.getByTickerCurr(value.ticker, 0);
        }
      }
    }
    return [totalCryptoInUsd, totalCryptoInArs];
  }

  changeCryptoPrices(crypto: any) {
    this.cryptoPrices$.next(crypto.prices);
  }

  swapBetweenCoins(amount: string, coinPair: string, operation: string, code: string) {
    const obs = this.api.createMarketOrder(amount, coinPair, operation, code);
    obs.subscribe((order) => {
      const truncatedAmount = Utils.roundDownForOperation(+order.amount);
      const isBuyingSwapPair = operation === 'BUY' ? true : false;
      const coinArr = coinPair.split('_');
      const srcCoin = isBuyingSwapPair ? coinArr[1] : coinArr[0];
      const destCoin = isBuyingSwapPair ? coinArr[0] : coinArr[1];
      const srcCoinAmount = isBuyingSwapPair ? truncatedAmount * order.price : truncatedAmount;
      const destCoinAmount = isBuyingSwapPair ? truncatedAmount : truncatedAmount * order.price;

      this.authService.loggedUser.orders.push(order);

      // Update user balance
      const cryptoBalance = this.authService.loggedUser.balance.crypto;
      // AUST vs FIAT
      if (coinPair === 'AUST_ARS' || coinPair === 'AUST_USD') {
        const currency = coinPair === 'AUST_ARS' ? 'ARS' : 'USD';
        let fiatBalance = this.authService.loggedUser.balance.fiat[currency];
        if (isBuyingSwapPair) {
          fiatBalance = fiatBalance - Utils.roundDownForOperation(srcCoinAmount);
        } else {
          fiatBalance = fiatBalance + Utils.roundDownForOperation(destCoinAmount);
        }
        this.authService.loggedUser.balance.fiat[currency] = fiatBalance;
      } else {
        // CRYPTO SWAP
        cryptoBalance[srcCoin].amount = new BigNumber(cryptoBalance[srcCoin].amount)
          .minus(srcCoinAmount)
          .toString();

        if (!cryptoBalance[destCoin]) {
          // Create new crypto balance.
          cryptoBalance[destCoin] = {
            amount: destCoinAmount.toString(),
          };
        } else {
          cryptoBalance[destCoin].amount = new BigNumber(cryptoBalance[destCoin].amount)
            .plus(destCoinAmount)
            .toString();

          if (srcCoin === 'AUST' && order.custodyFee > 0) {
            // AUST CASE, We have to subtract the custody fee.
            cryptoBalance[srcCoin].amount = new BigNumber(cryptoBalance[srcCoin].amount)
              .minus(order.custodyFee)
              .toString();
          }
        }
      }

      this.authService.loggedUser.balance.crypto = cryptoBalance;
      // Update portfolio
      let newCryptoPortfolio = this.authService.myCryptoPortfolio.value;
      if (coinPair === 'AUST_ARS' || coinPair === 'AUST_USD') {
        newCryptoPortfolio.forEach((balance) => {
          if (balance.ticker === coinPair) {
            const stocksValue = new BigNumber(balance.stocks);
            const balanceValue = new BigNumber(balance.balance);
            if (isBuyingSwapPair) {
              balance.stocks = stocksValue.plus(destCoinAmount).toString();
              balance.balance = balanceValue.plus(srcCoinAmount).toString();
            } else if (balance.ticker === coinPair) {
              balance.stocks = stocksValue.minus(srcCoinAmount).toString();
              balance.balance = balanceValue.minus(destCoinAmount).toString();
            }
          }
        });
      } else {
        const coinExistsInBalance = newCryptoPortfolio.some(
          (balance) => balance.ticker === destCoin,
        );

        const srcCoinPriceArs = new BigNumber(this.cryptoPrices$.value[srcCoin + '_ARS'].sell);
        const destCoinPriceArs = new BigNumber(this.cryptoPrices$.value[destCoin + '_ARS'].sell);

        newCryptoPortfolio.forEach((balance) => {
          const stocksValue = new BigNumber(balance.stocks);
          const balanceValue = new BigNumber(balance.balance);
          const priceValue = new BigNumber(balance.price);

          if (balance.ticker === srcCoin) {
            balance.stocks = stocksValue.minus(srcCoinAmount).toString();
            balance.balance = balanceValue.minus(priceValue.multipliedBy(srcCoinAmount)).toString();
            balance.balanceArs = new BigNumber(balance.stocks)
              .multipliedBy(srcCoinPriceArs)
              .toString();
          } else if (balance.ticker === destCoin && coinExistsInBalance) {
            balance.stocks = stocksValue.plus(destCoinAmount).toString();
            balance.balance = balanceValue.plus(priceValue.multipliedBy(destCoinAmount)).toString();
            balance.balanceArs = new BigNumber(balance.stocks)
              .multipliedBy(destCoinPriceArs)
              .toString();
          }
        });

        if (!coinExistsInBalance) {
          let destCoinPrice: string = this.cryptoPrices$.value[destCoin + '_USD'].sell;

          if (destCoin === 'AUST') {
            destCoinPrice = this.cryptoPrices$.value[destCoin + '_DAI'].sell;
          }
          const destCoinAmountValue = new BigNumber(destCoinAmount);
          const newBalance = {
            ticker: destCoin,
            name: COIN_FULL_NAME[destCoin.toUpperCase()],
            price: destCoinPriceArs.toString(),
            porcentage: 0,
            stocks: destCoinAmount.toString(),
            balance: destCoinAmountValue.multipliedBy(new BigNumber(destCoinPrice)).toString(),
            balanceArs: destCoinAmountValue.multipliedBy(destCoinPriceArs).toString(),
            stakeBalance: 0,
            totalARSinvested: destCoinAmountValue.multipliedBy(destCoinPriceArs).toNumber(),
            capitalChange: 0,
            capitalChangeP: 0,
            histories: [
              {
                name: destCoin,
                series: [
                  { value: 160, name: '2016-09-17T14:08:35.383Z' },
                  { value: 165, name: '2016-09-18T12:34:17.530Z' },
                  { value: 170, name: '2016-09-19T12:27:47.117Z' },
                  { value: 175, name: '2016-09-20T15:39:02.232Z' },
                  { value: 180, name: '2016-09-21T22:52:36.263Z' },
                  { value: 185, name: '2016-09-22T22:52:36.263Z' },
                  { value: 190, name: '2016-09-23T22:52:36.263Z' },
                ],
              },
            ],
          };
          newCryptoPortfolio.push(newBalance);
        }
      }

      newCryptoPortfolio = newCryptoPortfolio.filter((balance) => Number(balance.stocks) !== 0);
      this.updateBalancePercentage();
      this.authService.myCryptoPortfolio.next(newCryptoPortfolio);
      this.authService.loggedUserObs.next(this.authService.loggedUser);
    });
    return obs;
  }

  updateBalancePercentage() {
    // Recalculate % of all crpyto balances
    const balances = this.authService.myCryptoPortfolio.value;
    const totalInvestedArs = balances.reduce(
      (total, balance) => total + Number(balance.stocks) * Number(balance.price),
      0,
    );
    balances.forEach((balance) => {
      balance.porcentage = Math.round((Number(balance.balance) / Number(totalInvestedArs)) * 100);
    });
    this.authService.myCryptoPortfolio.next(balances);
  }

  setPortfolioByCryptoBalance() {
    const balance: {
      [coin: string]: { amount: string };
    } = this.authService.loggedUser?.balance.crypto;

    const cryptoPrices = this.cryptoPrices$.value;
    const coins = balance ? Object.keys(balance) : [];
    const newPortfolio: ICryptoPortfolio[] = [];
    coins.forEach((coin: string) => {
      const stocks: string = balance[coin].amount;
      if (coin === 'LUNA') {
        coin = 'LUNC';
      } else if (coin === 'LUNA2') {
        coin = 'LUNA';
      } else if (coin === 'UST') {
        coin = 'USTC';
      }
      if (Number(stocks) !== 0) {
        let coinPrices = cryptoPrices[coin + '_USD'];
        if (coin === 'AUST') {
          // Caso excepcional AUST, se muestra el precio en DAI porque no tiene precio en ARS ni USD (por ahora)
          coinPrices = cryptoPrices[coin + '_DAI'];
        }
        const coinPricesArs = cryptoPrices[coin + '_ARS'];
        const sellPriceArs = new BigNumber(coinPricesArs ? coinPricesArs.sell : '0');
        const sellPrice = new BigNumber(coinPrices ? coinPrices.sell : '0');
        const stocksValue = new BigNumber(stocks);

        const portfolioObj: ICryptoPortfolio = {
          ticker: coin,
          name: COIN_FULL_NAME[coin.toUpperCase()],
          price: sellPrice.toString(),
          porcentage: 0,
          stocks,
          balance: stocksValue.multipliedBy(sellPrice).toString(),
          balanceArs: stocksValue.multipliedBy(sellPriceArs).toString(),
          stakeBalance: 0,
          totalInvested: stocksValue.multipliedBy(sellPrice).toNumber(),
          totalARSinvested: stocksValue.multipliedBy(sellPriceArs).toNumber(),
          capitalChange: 0,
          capitalChangeP: coinPrices ? +coinPrices.variation?.daily : 0,
          histories: [
            {
              name: coin,
              series: [
                { value: 160, name: '2016-09-17T14:08:35.383Z' },
                { value: 165, name: '2016-09-18T12:34:17.530Z' },
                { value: 170, name: '2016-09-19T12:27:47.117Z' },
                { value: 175, name: '2016-09-20T15:39:02.232Z' },
                { value: 180, name: '2016-09-21T22:52:36.263Z' },
                { value: 185, name: '2016-09-22T22:52:36.263Z' },
                { value: 190, name: '2016-09-23T22:52:36.263Z' },
              ],
            },
          ],
        };
        newPortfolio.push(portfolioObj);
      }
    });
    this.authService.myCryptoPortfolio.next(newPortfolio);
    this.updateBalancePercentage();
  }

  applyBenefit(newBalance: any) {
    const balance = this.cryptoBalance.value;
    if (balance[newBalance.ticker]) {
      balance[newBalance.ticker].amount =
        Number(balance[newBalance.ticker].amount) + newBalance.benefit;
    } else {
      balance[newBalance.ticker] = {
        ticker: newBalance.ticker,
        amount: newBalance.benefit,
        stakeBalance: 0,
      };
    }
    this.cryptoBalance.next(balance);
    this.authService.loggedUser.benefitHistory.unshift({
      coin: COIN.DAI,
      creationTime: newBalance.time,
      saved: newBalance.benefit,
      source: newBalance.source ? newBalance.source : '',
      type: newBalance.type,
      _id: '1',
    });
  }

  getCoinFullName(ticker: string) {
    return COIN_FULL_NAME[ticker.toUpperCase()];
  }

  isStableCoin(ticker: string) {
    switch (ticker?.toUpperCase()) {
      case 'DAI':
        return true;
      case 'USDC':
        return true;
      case 'USDT':
        return true;
      case 'BUSD':
        return true;
      case 'STABLES':
        return true;
      default:
        return false;
    }
  }

  getValidSwapPair(srcTicker: string, destTicker: string) {
    /*
      Recibe dos COINs y retorna el nombre del price correcto.
       Ejemplo: Función recibe ETH y BTC, deberia retornar "ETH_BTC" o "BTC_ETH" dependiendo de como viene del objeto precio.
       Si no existe ninguno de los dos en el objeto precio, retorna string vacio.
    */
    const pairs = this.validSwapPairs.value;
    const isFirstPair = pairs.includes(srcTicker + '_' + destTicker);
    const isSecondPair = pairs.includes(destTicker + '_' + srcTicker);
    if (isFirstPair || isSecondPair) {
      return isFirstPair ? srcTicker + '_' + destTicker : destTicker + '_' + srcTicker;
    }
    return '';
  }

  isBuyingSwapPair(srcCoin: string, destCoin: string) {
    const swapPair = this.getValidSwapPair(srcCoin, destCoin);
    if (swapPair === '') return;
    return swapPair.split('_')[0] === destCoin;
  }

  lockMarketOrder(coin: string, operation: string, isSwap: boolean = false) {
    if (coin.includes('USTC')) {
      coin = coin.replace('USTC', 'UST');
    } else if (coin.includes('LUNC')) {
      coin = coin.replace('LUNC', 'LUNA');
    } else if (coin.split('_')[0] === 'LUNA') {
      coin = `${coin.split('_')[0]}2_${coin.split('_')[1]}`;
    }
    const coinArr = coin.split('_');
    if (this.authService.loggedUser?.isCommerce && !isSwap) {
      if (this.isStableCoin(coinArr[0]) || coinArr[0] == COIN_TICKER.NUARS) {
        coin = coin.replace(coinArr[0], `${coinArr[0]}COMM`);
      }
    }
    const obs = this.api.lockMarketOrder(coin, operation);
    return obs;
  }

  createMarketOrder(amount: string, coin: string, operation: string, code: string) {
    let commCoin = coin;
    const coinArr = coin.split('_');
    if (this.authService.loggedUser?.isCommerce) {
      if (this.isStableCoin(coinArr[0]) || coinArr[0] == COIN_TICKER.NUARS) {
        commCoin = coin.replace(coinArr[0], `${coinArr[0]}COMM`);
      }
    }
    if (commCoin.includes('USTC')) {
      commCoin = commCoin.replace('USTC', 'UST');
    } else if (commCoin.includes('LUNC')) {
      commCoin = commCoin.replace('LUNC', 'LUNA');
    } else if (commCoin.split('_')[0] === 'LUNA') {
      commCoin = `${commCoin.split('_')[0]}2_${commCoin.split('_')[1]}`;
    }
    const obs = this.api.createMarketOrder(amount, commCoin, operation, code);
    obs.subscribe((order) => {
      const coinArr = coin.split('_');
      const newOrder = {
        coin: coinArr[0], // DAI
        ticker: coin, // DAI_USD (if currency is USD)
        operation,
        amount: Utils.roundDownForOperation(+order.amount),
        currency: coinArr[1] === 'USD' ? 0 : 1,
      };
      this.authService.loggedUser.orders.push(order);

      const newAmountValue = new BigNumber(order.amount);
      const cryptoBalance = this.authService.loggedUser.balance.crypto;
      if (!cryptoBalance[newOrder.coin]) {
        cryptoBalance[newOrder.coin] = {
          amount: newAmountValue.toString(),
        };
        this.authService.loggedUser.balance.crypto = cryptoBalance;
      } else {
        const oldBalance = new BigNumber(cryptoBalance[newOrder.coin].amount);
        if (newOrder.operation === 'BUY') {
          cryptoBalance[newOrder.coin].amount = oldBalance.plus(newAmountValue).toString();
        } else {
          cryptoBalance[newOrder.coin].amount = oldBalance.minus(newAmountValue).toString();
        }
        this.authService.loggedUser.balance.crypto = cryptoBalance;
        this.setPortfolioByCryptoBalance();
      }

      const origAmount = order.amount * order.price;
      if (newOrder.operation === 'BUY') {
        if (newOrder.currency === 0) {
          this.authService.loggedUser.balance.fiat.USD = Utils.roundDownForSpecificDecimal(
            this.authService.loggedUser.balance.fiat.USD - origAmount,
            3,
          );
        } else {
          this.authService.loggedUser.balance.fiat.ARS = Utils.roundDownForSpecificDecimal(
            this.authService.loggedUser.balance.fiat.ARS - origAmount,
            2,
          );
        }
      } else {
        if (newOrder.currency === 0) {
          this.authService.loggedUser.balance.fiat.USD = Utils.roundDownForSpecificDecimal(
            this.authService.loggedUser.balance.fiat.USD + origAmount,
            3,
          );
        } else {
          this.authService.loggedUser.balance.fiat.ARS = Utils.roundDownForSpecificDecimal(
            this.authService.loggedUser.balance.fiat.ARS + origAmount,
            2,
          );
        }
      }

      this.setPortfolioByCryptoBalance();
      this.authService.saveUserToLocalStorage();
    });
    return obs;
  }

  updateTransactionToPortfolio(tx: ITransaction, operation: string) {
    const balance = this.authService.loggedUser.balance.crypto;
    if (balance[tx.coin]) {
      const currentAmount = new BigNumber(balance[tx.coin].amount);
      const txAmount = new BigNumber(tx.amount);

      if (operation === TransactionTypeString.DEPOSIT) {
        balance[tx.coin].amount = currentAmount.plus(txAmount).toString();
      } else {
        const txCost = new BigNumber(tx.cost);
        if (tx.status === TransactionStatus.CANCELLED) {
          balance[tx.coin].amount = BigNumber.sum(currentAmount, txAmount, txCost).toString();
        } else {
          balance[tx.coin].amount = currentAmount.minus(txAmount.plus(txCost)).toString();
        }
      }
    }
    if (!balance[tx.coin] && operation === TransactionTypeString.DEPOSIT) {
      balance[tx.coin] = { amount: tx.amount };
    }
    this.authService.loggedUser.balance.crypto = balance;
    this.setPortfolioByCryptoBalance();
  }

  sendWithdrawEmailVerification(params: {
    address: string;
    amount: string;
    coin: string;
    chain: string;
    costCode: string;
  }) {
    const obs = this.api.sendWithdrawEmailVerification(params);
    return obs;
  }

  withdrawCrypto(code: string) {
    const obs = this.api.withdrawCrypto(code);
    obs.subscribe((res) => {
      this.authService.loggedUser.transactions.push(res);

      // Update history

      // Update balance
      this.updateTransactionToPortfolio(res, TransactionTypeString.WITHDRAW);
    });
    return obs;
  }

  getWithdrawCost(ticker: string, wallet: string) {
    const obs = this.api.getWithdrawCost(ticker, wallet);
    return obs;
  }

  private initializeMarketCoins() {
    const stablesCoins = [];
    const volatilCoins = [];
    const marketCoins = Object.keys(this.cryptoPrices$.value);
    marketCoins.forEach((coin) => {
      const coinSymbol = coin.split('_')[0];
      // if(coinSymbol === 'LUNA' || coinSymbol === 'UST'){
      //   coinSymbol = `${coinSymbol[0]}C`;
      //   coin = `${coin}C`;
      // }
      let currency = 3;
      if (coin.split('_')[1] === 'ARS') {
        currency = 1;
      } else if (coin.split('_')[1] === 'USD') {
        currency = 0;
      }
      const coinObj = {
        symbol: coin,
        ticker: coinSymbol,
        color: {
          domain: [COIN_COLOR[coinSymbol]],
        },
        currency,
        price: (+this.cryptoPrices$.value[coin].buy + +this.cryptoPrices$.value[coin].sell) / 2,
        sellPrice: this.cryptoPrices$.value[coin].sell,
        buyPrice: this.cryptoPrices$.value[coin].buy,
        buyDailyChange: +this.cryptoPrices$.value[coin].variation?.daily || 0,
        sellDailyChange: +this.cryptoPrices$.value[coin].variation?.daily || 0,
        quantity: 0,
        active: true,
        histories: [
          [
            {
              name: coinSymbol,
              series: [],
            },
          ],
        ],
        minMax: [
          {
            min: 0,
            max: 1,
          },
        ],
      };
      if (this.isStableCoin(coinSymbol)) {
        stablesCoins.push(coinObj);
      } else {
        volatilCoins.push(coinObj);
      }
    });
    this.stableCryptos.next(stablesCoins);
    this.volatilCryptos.next(volatilCoins);
  }

  getBlockchainStatus() {
    const obs = this.api.getBlockchainStatus();
    obs.subscribe({
      next: (status) => {
        let availableCoinsForWithdraw = [];
        status
          .filter((chain) => chain.type === 1)
          .forEach((chain) => {
            availableCoinsForWithdraw.push(
              Object.keys(chain.coins).filter((coin) => chain.coins[coin] === true),
            );
          });
        availableCoinsForWithdraw = [...new Set(availableCoinsForWithdraw.flat())].map((coin) => {
          //replace terra tickers
          if (coin === 'LUNA') {
            coin = 'LUNC';
          } else if (coin === 'LUNA2') {
            coin = 'LUNA';
          } else if (coin === 'UST') {
            coin = 'USTC';
          }
          return coin;
        });
        this.withdrawableCoins$.next(availableCoinsForWithdraw);
      },
    });
    return obs;
  }

  getOrdersStatus() {
    return this.api.getOrdersStatus();
  }

  getPriceCoin(companyId: string, coin: string) {
    return this.api.getPriceCoin(companyId, coin);
  }

  lockPriceWhitelabel(
    coin: 'USDC' | 'WLD' | 'USDT' | 'USDCB',
    amount: number,
    lockType: 'ADDRESS' | 'CUIT',
    against: ACCOUNT_CURRENCY = ACCOUNT_CURRENCY.ARS,
    chain: number,
    payload: { externalId: string | undefined; withdrawType: string },
  ) {
    return this.api.lockPriceWhitelabel(coin, amount, lockType, against, chain, payload);
  }

  getTransaction(hash: string) {
    return this.api.getTransaction(hash);
  }

  setMinOperation(minOperation: IMinOperation) {
    this._minOperation.set(minOperation);
  }

  setErrorLimit(isError: boolean) {
    this._errorLimit.set(isError);
  }

  loadMinOperationFromLS() {
    const minOperation = localStorage.getItem('min-operation');
    if (minOperation) {
      const data = JSON.parse(minOperation);
      this._minOperation.set(data);
    }
  }
}
